<template>
  <div class="page-container">
    <div ref="helpDetailPage" class="help-detail-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <!-- Page header -->
      <PageHeader :config="config" @module-click="handleModuleClick" />

      <!-- Breadcrumb navigation -->
      <Breadcrumb :items="breadcrumbItems" />

      <!-- Main content -->
      <div class="detail-container">
        <!-- Header banner area -->
        <DetailBanner
          :background-image="detailBannerBg"
          :collect-label="t('detailBanner.collectLabel')"
          :collected-label="t('detailBanner.collectedLabel')"
          :collect-count="0"
          :collect-unit="t('detailBanner.collectUnit')"
          :showCollectInfo="false"
        >
          <div class="help-banner-content">
            <div class="banner-left">
            <h1 class="banner-title">{{ articleInfo.title }}</h1>
              <div class="banner-date">{{ articleInfo.date }}</div>
            </div>
          
          </div>
        </DetailBanner>

        <!-- Main content area -->
        <div class="detail-content">
          <!-- Left content -->
          <div class="content-left">
            <!-- Content area -->
            <div class="content-section">
              <div class="content-text" v-html="articleInfo.content || ''"></div>
            </div>
          </div>

          <!-- Right content -->
          <div class="content-right">
         

            <!-- Related questions -->
            <div class="related-section">
              <div class="section-title">
                <span class="title-zh">{{ t('helpDetailPage.relatedQuestionsTitle') }}</span>
                <span class="title-en">RELATED ISSUES</span>
              </div>
              <div class="related-list">
                <div 
                  v-for="(item, index) in relatedArticles" 
                  :key="item.id || index"
                  class="related-item"
                  @click="handleRelatedClick(item)"
                >
                  <img src="@/assets/img/help/item-left-ico.png" alt="" class="related-icon" />
                  <div class="related-content">
                    <div class="related-title">{{ item.title }}</div>
                    <div class="related-date">{{ item.date }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { localeImg } from '@/utils/localeAsset'
import { getHomeConfig } from '@/config/homeConfig'
import { getHelpConfig } from '@/config/helpConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import DetailBanner from '@/components/DetailBanner.vue'
import helpApi from '@/api/help'
import { getText, getCurrentLocale } from '@/utils/i18nText'
import { behaviorPageMixin } from '@/mixins/behaviorPage'

export default {
  name: 'HelpDetail',
  mixins: [behaviorPageMixin('help')],
  components: {
    PageFooter,
    PageHeader,
    Breadcrumb,
    DetailBanner
  },
  data() {
    return {
      config: {},
      helpConfig: {},
      articleInfo: {
        id: '',
        title: '',
        date: '',
        content: ''
      },
      relatedArticles: [],
      breadcrumbItems: [
        { label: '', path: '/index' },
        { label: '', path: '/help' },
        { label: '', path: '' }
      ],
      qaGraphic: localeImg('help/help-text.png'),
      bannerBg: null
    }
  },
  computed: {
    pageCfg() {
      return this.helpConfig.help || {}
    },
    detailBannerBg() {
      return this.bannerBg || this.pageCfg.assets?.bannerBg || localeImg('help/head-bg.png')
    }
  },
  created() {
    this.loadConfig()
    this.loadArticleDetail()
    this.loadRelatedArticles()
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  watch: {
    '$route'(to, from) {
      if (to.params.id !== from.params.id) {
        this.loadArticleDetail()
        this.loadRelatedArticles()
      }
    }
  },
  methods: {
    t(path) {
      return getText(path)
    },
    // Set scale ratio
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },

    getPageScaleEl() {
      return this.$refs.helpDetailPage
    },

    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },

    getBehaviorTargetAttrs() {
      return {
        targetId: String(this.articleInfo.id || this.$route.params.id || this.$route.query.id || ''),
        targetTitle: this.articleInfo.title || '',
        domain: ''
      }
    },
    // Load config
    loadConfig() {
      const lang = getCurrentLocale()
      this.config = getHomeConfig(lang)
      this.helpConfig = getHelpConfig(lang)

      // Breadcrumbs
      const breadcrumb = this.pageCfg.breadcrumb || {}
      this.breadcrumbItems = [
        { label: breadcrumb.home || '', path: '/index' },
        { label: breadcrumb.current || '', path: '/help' },
        { label: this.pageCfg.detail?.breadcrumb || '', path: '' }
      ]

      // Banner background image
      this.bannerBg = this.pageCfg.assets?.bannerBg || localeImg('help/head-bg.png')
    },

    // Load article detail
    async loadArticleDetail() {
      this.behaviorViewReported = false
      const id = this.$route.params.id || this.$route.query.id
      const content = this.$route.query.content
      
      // If content is passed from list page, use it directly
      if (content !== undefined) {
        this.articleInfo = {
          id: id || '',
          title: this.$route.query.title || '',
          date: this.$route.query.date || this.formatDate(new Date()),
          content: decodeURIComponent(content || '')
        }
        this.reportBehaviorPageView()
        return
      }
      
      if (!id) {
        // If no id, use mock data
        this.initMockData()
        return
      }

      try {
        const response = await helpApi.getHelpDetail(id)
        const result = response.data || {}
        
        if (result.status === 0 && result.data) {
          const data = result.data
          this.articleInfo = {
            id: data.id || id,
            title: data.title || data.name || '',
            date: this.formatDate(data.ctime || data.utime || data.createTime || data.date),
            content: data.content || ''
          }
          this.reportBehaviorPageView()
        } else {
          this.initMockData()
        }
      } catch (error) {
        console.error('Failed to load help article detail:', error)
        this.initMockData()
      }
    },

    // Format date
    formatDate(dateStr) {
      if (!dateStr) return '2025-11-20'
      
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return dateStr
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    // Initialize mock data
    initMockData() {
      const id = this.$route.params.id || this.$route.query.id || ''
      const title = this.$route.query.title || ''
      
      this.articleInfo = {
        id: id,
        title: title,
        date: '2015-11-05',
        content: `<p>${this.t('helpDetailPage.noContent')}</p>`
      }
      this.reportBehaviorPageView()
    },


    // Load related questions
    async loadRelatedArticles() {
      const id = this.articleInfo.id || this.$route.params.id || this.$route.query.id
      if (!id) {
        this.initMockRelated()
        return
      }

      try {
        const response = await helpApi.getHelpList({
          page: 0,
          pageSize: 3
        })
        const result = response.data || {}
        
        if (result.status === 0 && result.data) {
          const data = result.data
          const list = Array.isArray(data.rows) ? data.rows : (Array.isArray(data) ? data : [])
          
          // Filter out current article
          const filtered = list.filter(item => {
            const itemId = item.id || ''
            return itemId && itemId !== id
          }).slice(0, 3)
          
          this.relatedArticles = filtered.map(item => ({
            id: item.id || '',
            title: item.title || item.name || '',
            date: this.formatDate(item.ctime || item.utime || item.createTime || item.date),
            content: item.content || ''
          }))
        } else {
          this.initMockRelated()
        }
      } catch (error) {
        console.error('Failed to load related questions:', error)
        this.initMockRelated()
      }
    },

    // Initialize mock related questions
    initMockRelated() {
      this.relatedArticles = [
        {
          id: '1',
          title: this.t('helpDetailPage.mockRegisterFAQTitle'),
          date: '2025-11-20'
        },
        {
          id: '2',
          title: this.t('helpDetailPage.mockDemandPublishFAQTitle'),
          date: '2025-11-20'
        },
        {
          id: '3',
          title: this.t('helpDetailPage.mockAchievementPublishFAQTitle'),
          date: '2025-11-20'
        }
      ]
    },

    // Related question click
    handleRelatedClick(item) {
      this.$router.push({
        name: 'helpDetail',
        params: { id: item.id },
        query: { 
          title: item.title,
          content: item.content || '' // Pass content data
        }
      })
    },

    // Module click
    handleModuleClick(module) {
      this.$emit('module-click', module)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/mixins/portal-rich-text-wrap.scss';
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.help-detail-page {
  width: 1920px;
  min-height: 100vh;
  background: #ffffff;
  transform-origin: top left;
}

.detail-container {
  width: 1700px;
  margin: 30px auto 50px;
}

// Banner content styles
.help-banner-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 200px;
  
  .banner-left {
    flex: 1;
    min-width: 0;
    
    .banner-title {
      font-family: Source Han Sans CN;
      font-weight: 600;
      font-size: 28px;
      color: #333333;
      margin: 0 0 12px 0;
      line-height: 1.4;
    }
    
    .banner-date {
      font-family: Source Han Sans CN;
      font-weight: 400;
      font-size: 14px;
      color: #8E7E29;
    }
  }
  
  .banner-right {
    flex-shrink: 0;
    margin-left: 40px;
    
    .qa-icon-wrapper {
      width: 200px;
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .qa-icon {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
  }
}

.detail-content {
  display: flex;
  gap: 40px;
  margin-top: 30px;
  
  .content-left {
    flex: 1;
    min-width: 0;
    
    .content-section {
      .content-text {
        @include portal-rich-text-wrap;
        font-family: Source Han Sans CN;
        font-weight: 400;
        font-size: 14px;
        color: #666666;
        line-height: 1.8;
        
        // Handle HTML content styles
        :deep(p) {
          margin: 0 0 16px 0;
          &:last-child {
            margin-bottom: 0;
          }
        }
        
        :deep(img) {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 16px 0;
        }
        
        :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
          font-family: Source Han Sans CN;
          font-weight: 500;
          color: #333333;
          margin: 20px 0 12px 0;
          &:first-child {
            margin-top: 0;
          }
        }
        
        :deep(ul), :deep(ol) {
          margin: 16px 0;
          padding-left: 24px;
        }
        
        :deep(li) {
          margin: 8px 0;
        }
      }
    }
  }
  
  .content-right {
    width: 320px;
    flex-shrink: 0;
    
    .qa-graphic {
      width: 100%;
      margin-bottom: 40px;
      
      img {
        width: 100%;
        height: auto;
        display: block;
      }
    }
    
    .related-section {
      .section-title {
        margin-bottom: 20px;
        
        .title-zh {
          display: block;
          font-family: Source Han Sans CN;
          font-weight: 500;
          font-size: 18px;
          color: #333333;
          margin-bottom: 4px;
        }
        
        .title-en {
          display: block;
          font-family: Source Han Sans CN;
          font-weight: 400;
          font-size: 12px;
          color: #999999;
        }
      }
      
      .related-list {
        .related-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          border: 1px solid rgba(160, 144, 60, 0.2);
          border-radius: 4px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.3s;
          
          &:hover {
            border-color: rgba(160, 144, 60, 0.4);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
          
          .related-icon {
            width: 24px;
            height: 24px;
            flex-shrink: 0;
          }
          
          .related-content {
            flex: 1;
            min-width: 0;
            
            .related-title {
              font-family: Source Han Sans CN;
              font-weight: 400;
              font-size: 14px;
              color: #333333;
              margin-bottom: 4px;
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              line-height: 1.4;
            }
            
            .related-date {
              font-family: Source Han Sans CN;
              font-weight: 400;
              font-size: 12px;
              color: #999999;
            }
          }
        }
      }
    }
  }
}

</style>
